import { useMemo } from "react";
import { motion } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles } from "lucide-react";
import {
    useCatalog,
    formatUSD,
    findPackageByName,
    getAddonsForPackage,
    computePricing,
} from "@/lib/CatalogProvider";
import { SectionHead } from "@/components/inquiry/inquiryFields";
import PricingSummary from "@/components/site/PricingSummary";

/**
 * StepAddons — curated, package-scoped add-ons + live pricing summary.
 *
 * When Custom Engagement is chosen the picker is hidden and the pricing
 * card falls back to “Custom Quote Required”.
 */
export const StepAddons = ({ data, onToggleAddon }) => {
    const { packages, addons } = useCatalog();

    const selectedPkg = useMemo(
        () => (data.is_custom_scope ? null : findPackageByName(packages, data.selected_package)),
        [packages, data.selected_package, data.is_custom_scope]
    );

    const availableAddons = useMemo(
        () => (selectedPkg ? getAddonsForPackage(addons, selectedPkg) : []),
        [selectedPkg, addons]
    );

    const selectedAddonObjects = useMemo(
        () => (availableAddons || []).filter((a) => (data.addons || []).includes(a.id)),
        [availableAddons, data.addons]
    );

    const pricing = useMemo(
        () => computePricing({
            selectedPackage: selectedPkg,
            selectedAddons: selectedAddonObjects,
            isCustomScope: data.is_custom_scope,
        }),
        [selectedPkg, selectedAddonObjects, data.is_custom_scope]
    );

    const accent = selectedPkg?.kind === "video" ? "cyan" : "gold";

    return (
        <section data-testid="step-addons">
            <SectionHead
                eyebrow="Step 03 · Add-ons"
                title="Refine your scope."
                description="Optional — pick anything that sharpens the outcome. You can always adjust with Pranto once we talk."
            />

            {data.is_custom_scope ? (
                <div
                    className="rounded-2xl border p-6"
                    style={{
                        borderColor: "color-mix(in srgb, var(--pg-accent) 30%, var(--pg-border))",
                        background: "var(--pg-surface)",
                    }}
                    data-testid="addons-custom-notice"
                >
                    <div className="inline-flex items-center gap-2">
                        <Sparkles className="h-4 w-4" style={{ color: "var(--pg-accent)" }} />
                        <span className="text-[11px] font-medium tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>
                            Custom Project
                        </span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                        Add-ons are skipped in a Custom Project — we tailor the deliverables directly to your goals.
                        You can continue to the next step.
                    </p>
                </div>
            ) : (
                <>
                    {availableAddons.length === 0 ? (
                        <p className="rounded-2xl border p-6 text-sm" style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)", color: "var(--pg-fg-muted)" }} data-testid="addons-empty">
                            No add-ons available for this package. Continue to the next step.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="addons-list">
                            {availableAddons.map((a, idx) => {
                                const active = (data.addons || []).includes(a.id);
                                return (
                                    <motion.label
                                        key={a.id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.35, delay: 0.03 * idx }}
                                        htmlFor={`addon-${a.id}`}
                                        data-testid={`addon-${a.id}`}
                                        data-addon-id={a.id}
                                        className="flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-all"
                                        style={{
                                            borderColor: active ? "var(--pg-accent)" : "var(--pg-border)",
                                            background: active ? "color-mix(in srgb, var(--pg-accent) 8%, transparent)" : "var(--pg-surface)",
                                        }}
                                    >
                                        <Checkbox
                                            id={`addon-${a.id}`}
                                            checked={active}
                                            onCheckedChange={() => onToggleAddon(a.id)}
                                            className="mt-0.5"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-display text-sm" style={{ color: "var(--pg-fg)" }}>{a.name}</span>
                                                <span className="font-mono text-[11px]" style={{ color: active ? "var(--pg-accent)" : "var(--pg-fg-muted)" }}>
                                                    +{formatUSD(a.price)}
                                                </span>
                                            </div>
                                            {a.description && (
                                                <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                                                    {a.description}
                                                </p>
                                            )}
                                        </div>
                                    </motion.label>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            <div className="mt-8">
                <PricingSummary
                    selectedPackage={selectedPkg}
                    selectedAddons={selectedAddonObjects}
                    basePrice={pricing.basePrice}
                    addOnsTotal={pricing.addOnsTotal}
                    estimatedTotal={pricing.estimatedTotal}
                    isCustomScope={data.is_custom_scope}
                    accent={accent}
                    testIdPrefix="addons-pricing-summary"
                />
            </div>
        </section>
    );
};

export default StepAddons;
